import type { AdditionalSubmissionQuestionData } from '@jobilla/nexus-sdk'
import { useTranslation } from 'react-i18next'
import { FileItem } from './candidate-files'

export type CandidateAdditionalAnswersProps = {
	questions: AdditionalSubmissionQuestionData[]
}

function FileAnswerDisplay({ answers }: { answers: AdditionalSubmissionQuestionData['answers'] }) {
	const fileAnswers = answers.filter(answer => answer.file)

	if (fileAnswers.length === 0) {
		return <span>(no file uploaded)</span>
	}

	return (
		<div className="flex flex-col gap-1.5">
			{fileAnswers.map(answer => answer.file && <FileItem key={answer.id} file={answer.file} />)}
		</div>
	)
}

export function CandidateAdditionalAnswers({ questions }: CandidateAdditionalAnswersProps) {
	const { t } = useTranslation('common')

	if (questions.length === 0) {
		return null
	}

	return (
		<div className="mt-6 pt-6 border-t border-gray-200">
			<h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-4">
				{t('candidateAdditionalAnswers.title')}
			</h3>
			<div className="divide-y divide-gray-100">
				{questions.map((question) => (
					<div key={question.id} className="px-2 py-4 last:pb-0">
						<div className="flex items-start gap-2 mb-2">
							<span className="size-5 rounded-full bg-brand-100 text-brand-700 text-xs font-medium flex items-center justify-center flex-shrink-0 mt-0.5">
								+
							</span>
							<p className="text-sm font-medium text-gray-700">
								{question.questionLabel ?? t('candidateAdditionalAnswers.unknownQuestion')}
							</p>
						</div>
						<div className="ph-no-capture ml-7">
							{question.questionType === 'file'
								? <FileAnswerDisplay answers={question.answers} />
								: question.answers.filter(a => a.answer).length > 0
								? (
									<div className="flex flex-col gap-1">
										{question.answers.filter(a => a.answer).map((a, i) => (
											<div
												key={i}
												className="inline-flex items-center text-sm text-gray-600"
												style={{ width: 'fit-content' }}
											>
												<span className="mr-2 text-brand-400">→</span>
												{a.answer}
											</div>
										))}
									</div>
								)
								: (
									<span className="text-sm text-gray-600">
										{t('candidateAdditionalAnswers.noAnswer')}
									</span>
								)}
						</div>
					</div>
				))}
			</div>
		</div>
	)
}
